import React, { useEffect, useState, useRef } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  addDoc,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { fetchUsersByField, tolerantId } from "../../utils/firestoreHelpers";
import { sendIncidentClientWhatsApp } from "../../utils/onboardingInvite";
import { sendIncidentWhatsAppDirect, HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import { useToast } from "../../components/Toast";
import { colors, typography } from "../../utils/designTokens";
import styles from "./IncidenciasEdificio.module.css";
import ScreenHeader from "../../components/ScreenHeader";
import {
  FaExclamationTriangle,
  FaLockOpen,
  FaCheckCircle,
  FaBuilding,
  FaClipboardList,
  FaUser,
  FaClock,
  FaExclamationCircle,
  FaReply,
  FaHistory,
  FaTimes,
  FaEraser,
  FaFlag,
  FaList,
  FaChevronDown,
  FaBath,
  FaBolt,
  FaShieldAlt,
  FaBroom,
  FaTag,
  FaArrowUp,
  FaWhatsapp,
} from "react-icons/fa";


// SLA configurable por prioridad (en horas)
// Para cambiar los umbrales, modificar estos valores
const SLA_CONFIG = {
  alta:  { warningHours: 24, criticalHours: 48, label: "48h" },
  media: { warningHours: 48, criticalHours: 72, label: "72h" },
  baja:  { warningHours: 96, criticalHours: 168, label: "7 días" },
};

/** Unifica valores en Firestore (p. ej. "Abierta") con el modelo de la pantalla admin. */
function normalizeIncidentStatus(raw) {
  const s = (raw ?? "").toString().trim();
  if (!s) return "Reportada";
  const lower = s.toLowerCase();
  if (["abierta", "abierto", "open"].includes(lower)) return "Reportada";
  if (["reportada", "reportado"].includes(lower)) return "Reportada";
  if (["en proceso", "en curso"].includes(lower)) return "En Proceso";
  if (lower === "pendiente") return "Pendiente";
  if (["resuelta", "resuelto", "cerrada", "cerrado", "closed"].includes(lower)) return "Resuelta";
  if (["Reportada", "En Proceso", "Pendiente", "Resuelta"].includes(s)) return s;
  return "Reportada";
}

function formatStatusLabel(canonical) {
  switch (canonical) {
    case "Reportada":
      return "Abierta";
    case "En Proceso":
      return "En proceso";
    case "Pendiente":
      return "Pendiente";
    case "Resuelta":
      return "Resuelta";
    default:
      return canonical || "—";
  }
}

function isIncidentResolved(inc) {
  return normalizeIncidentStatus(inc?.status || inc?.state) === "Resuelta";
}

/** true si Firestore negó la operación por reglas. */
function esPermisoDenegado(e) {
  const code = String(e?.code || "");
  return code === "permission-denied" || code.endsWith("/permission-denied");
}

const IncidenciasEdificio = ({ setSelected }) => {
  const { showToast } = useToast();
  const { selectedBuilding, buildings, setSelectedBuilding } = useBuilding();
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIncidencia, setSelectedIncidencia] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseInternal, setResponseInternal] = useState("");
  const [responseClient, setResponseClient] = useState("");
  const [updating, setUpdating] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [incidentHistory, setIncidentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Última nota interna de la incidencia abierta en el detalle (C4).
  const [lastInternalNote, setLastInternalNote] = useState(null);
  // B6 — resumen de hilo con IA
  const [threadSummary, setThreadSummary] = useState(null);
  const [summarizingThread, setSummarizingThread] = useState(false);
  // D2 — diagnostico visual de imagen
  const [imageDiagnosis, setImageDiagnosis] = useState(null);
  const [diagnosingImage, setDiagnosingImage] = useState(false);

  // D2 — Precargar el diagnostico persistido si la Cloud Function ya lo
  // analizo al crearse la incidencia. Asi el admin ve el card listo sin
  // tener que clickear "Diagnosticar". El boton "Re-analizar" sigue
  // disponible para forzar una nueva llamada.
  useEffect(() => {
    if (selectedIncidencia?.aiDiagnosis) {
      setImageDiagnosis(selectedIncidencia.aiDiagnosis);
    } else {
      setImageDiagnosis(null);
    }
  }, [selectedIncidencia?.id]);
  // B1 — Respuestas sugeridas (Fase 2)
  const [replySuggestions, setReplySuggestions] = useState(null); // {suggestions, firm_tone_recommended}
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedSuggestionTone, setSelectedSuggestionTone] = useState(null);
  const [originalSuggestionText, setOriginalSuggestionText] = useState("");

  /**
   * D2 — Llama al endpoint Claude vision para diagnosticar la foto de la
   * incidencia. Si ya hay un diagnóstico previo, se reusa (no se vuelve a
   * cobrar la llamada).
   */
  const handleDiagnoseImage = async () => {
    if (!selectedIncidencia?.imageUrl) {
      showToast("Esta incidencia no tiene foto adjunta.", "warning");
      return;
    }
    const buildingIdForCall =
      selectedIncidencia.buildingid ?? buildingFilter;
    if (!buildingIdForCall) {
      showToast("No se pudo determinar el edificio.", "warning");
      return;
    }
    try {
      setDiagnosingImage(true);
      setImageDiagnosis(null);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/diagnose-incident-image`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(buildingIdForCall),
          image_url: selectedIncidencia.imageUrl,
          user_description: selectedIncidencia.description || "",
          incident_type: selectedIncidencia.issue || "",
          incident_id: selectedIncidencia.id,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo analizar la imagen.", "error");
        return;
      }
      const data = await resp.json();
      setImageDiagnosis(data);
      // Persistir en Firestore para que quede asociado a la incidencia y
      // los siguientes admins lo vean precargado sin re-llamar al LLM.
      try {
        await updateDoc(doc(db, "incidents", selectedIncidencia.id), {
          aiDiagnosis: data,
          aiDiagnosisAt: serverTimestamp(),
          aiDiagnosisSource: "manual_admin",
        });
      } catch (saveErr) {
        console.warn("No se pudo persistir aiDiagnosis:", saveErr);
      }
    } catch (e) {
      console.error("diagnose-incident-image error:", e);
      showToast(e.message || "Error inesperado al analizar la imagen.", "error");
    } finally {
      setDiagnosingImage(false);
    }
  };
  const [activeTab, setActiveTab] = useState("abiertas");
  const [filterPriority, setFilterPriority] = useState("todas");
  const [filterType, setFilterType] = useState("todas");
  const [filterPriorityOpen, setFilterPriorityOpen] = useState(false);
  const [filterTypeOpen, setFilterTypeOpen] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [improvingAI, setImprovingAI] = useState(false);
  const filterPriorityRef = useRef(null);
  const filterTypeRef = useRef(null);

  /**
   * Llama a /api/improve-resident-message con el borrador actual de
   * `responseClient` y reemplaza el textarea con la versión mejorada por IA.
   * Si no hay borrador, avisa al admin.
   */
  const handleImproveWithAI = async () => {
    const draft = (responseClient || "").trim();
    if (!draft) {
      showToast("Escribí un borrador primero — la IA lo mejora, no lo crea desde cero.", "warning");
      return;
    }
    if (draft.length > 8000) {
      showToast("El texto es demasiado largo (máx. 8000 caracteres).", "warning");
      return;
    }

    try {
      setImprovingAI(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/improve-resident-message`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          text: draft,
          incident_title: selectedIncidencia?.title || "",
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo mejorar el texto. Intentá de nuevo.", "error");
        return;
      }
      const data = await resp.json();
      const improved = (data?.improved_text || "").trim();
      if (!improved) {
        showToast("La IA no devolvió texto. Volvé a intentar.", "error");
        return;
      }
      setResponseClient(improved);
      showToast("Texto mejorado con IA. Revisá antes de enviar.", "success");
    } catch (e) {
      console.error("improve-resident-message error:", e);
      showToast(e.message || "Error inesperado al mejorar con IA.", "error");
    } finally {
      setImprovingAI(false);
    }
  };

  /**
   * B1 — Pide al backend 3 borradores de respuesta sugeridos para esta
   * incidencia (cordial / detallada / firme). Usa el estilo del staff (H3).
   * El staff hace click en un chip y se llena el textarea.
   */
  const handleFetchSuggestions = async () => {
    if (!selectedIncidencia) return;
    const buildingIdForCall = selectedIncidencia.buildingid ?? buildingFilter;
    if (!buildingIdForCall) {
      showToast("No se pudo determinar el edificio de la incidencia.", "warning");
      return;
    }
    try {
      setLoadingSuggestions(true);
      setReplySuggestions(null);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/suggest-incident-reply`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          incident_id: selectedIncidencia.id,
          building_id: String(buildingIdForCall),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudieron generar sugerencias.", "error");
        return;
      }
      const data = await resp.json();
      setReplySuggestions(data);
    } catch (e) {
      console.error("suggest-incident-reply error:", e);
      showToast(e.message || "Error inesperado generando sugerencias.", "error");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  /**
   * B1 — Aplica una sugerencia al textarea. Recordamos el texto original
   * para luego (en handleResponseSubmit) calcular cuánto se editó.
   */
  const applySuggestion = (suggestion) => {
    if (!suggestion?.text) return;
    setResponseClient(suggestion.text);
    setSelectedSuggestionTone(suggestion.tone);
    setOriginalSuggestionText(suggestion.text);
  };

  /**
   * B1 — Tras enviar la respuesta, notifica al backend para que H3 decida si
   * guardar como style_example. No bloquea el flujo (fire-and-forget).
   */
  const recordReplyApproved = async (finalText) => {
    if (!selectedIncidencia || !finalText) return;
    const buildingIdForCall = selectedIncidencia.buildingid ?? buildingFilter;
    if (!buildingIdForCall) return;
    try {
      const headers = await getAuthHeaders();
      await fetch(`${HABI_API_BASE_URL}/api/record-incident-reply-approved`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          incident_id: selectedIncidencia.id,
          building_id: String(buildingIdForCall),
          capability: "suggest_incident_reply",
          original_suggestion: originalSuggestionText || "",
          final_output: finalText,
          selected_tone: selectedSuggestionTone || "",
        }),
      });
    } catch (e) {
      // No es crítico — solo es aprendizaje de memoria.
      console.warn("record-incident-reply-approved error:", e);
    }
  };

  /**
   * B6 — Resume el hilo de la incidencia abierta con Claude Haiku. Útil
   * cuando vuelvo de licencia o me toca una incidencia que ya tiene varios
   * mensajes y no quiero leer todo.
   */
  const handleSummarizeThread = async () => {
    if (!selectedIncidencia) return;
    const buildingIdForCall =
      selectedIncidencia.buildingid ?? buildingFilter;
    if (!buildingIdForCall) {
      showToast("No se pudo determinar el edificio de la incidencia.", "warning");
      return;
    }
    try {
      setSummarizingThread(true);
      setThreadSummary(null);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/summarize-incident-thread`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          incident_id: selectedIncidencia.id,
          building_id: String(buildingIdForCall),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo resumir el hilo. Intentá de nuevo.", "error");
        return;
      }
      const data = await resp.json();
      setThreadSummary(data);
    } catch (e) {
      console.error("summarize-incident-thread error:", e);
      showToast(e.message || "Error inesperado al resumir el hilo.", "error");
    } finally {
      setSummarizingThread(false);
    }
  };

  /**
   * Envía el mensaje al residente. Estrategia:
   * 1. Intenta enviar DIRECTO desde el número Habitia (Cloud Function
   *    sendIncidentResponseWhatsApp con Meta Cloud API).
   * 2. Si la Cloud Function no está desplegada / no configurada / falla,
   *    cae a wa.me (abre WhatsApp Web/app del staff).
   * Devuelve { ok: boolean, mode: "direct"|"wa_link"|"none" }.
   */
  /**
   * Polling del doc en `messages` para confirmar el estado real del envío
   * después del 200 OK inicial de Meta. Hace hasta 5 intentos (cada 3s)
   * buscando el doc por metaMessageId. Si el status pasa a "failed"
   * muestra toast nuevo con el motivo (típicamente error 131047 ventana 24h).
   * C3: la consulta fija también el buildingId que graba el servidor; sin él
   * las reglas no dejan al admin leer `messages`.
   */
  const pollWhatsAppDelivery = async (messageId, buildingId) => {
    if (!messageId || buildingId == null || buildingId === "") return;
    const maxAttempts = 5;
    const delay = 3000; // 3s entre intentos
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, delay));
      try {
        const q = query(
          collection(db, "messages"),
          where("metaMessageId", "==", messageId),
          where("buildingId", "in", tolerantId(buildingId)),
        );
        let snap = await getDocs(q);
        if (snap.empty) {
          // TRANSITORIO (hasta publicar 0D): con las reglas vigentes el staff
          // lee messages sin filtro, y un envío de un servidor que todavía no
          // graba buildingId solo se encuentra por metaMessageId. Con 0D esta
          // consulta se niega y se ignora.
          snap = await getDocs(
            query(collection(db, "messages"), where("metaMessageId", "==", messageId))
          ).catch(() => snap);
        }
        if (snap.empty) continue;
        const data = snap.docs[0].data();
        const status = (data.status || "").toLowerCase();
        if (status === "delivered" || status === "read") {
          showToast(`✅ Mensaje entregado al residente.`, "success");
          return;
        }
        if (status === "failed") {
          const reason = data.failureReason || "";
          const isWindowClosed =
            reason.includes("131047") ||
            reason.toLowerCase().includes("re-engagement") ||
            reason.toLowerCase().includes("24 hours");
          if (isWindowClosed) {
            showToast(
              `⚠️ Meta rechazó el envío: el residente no le escribió a Habitia en las últimas 24h. Solución: que el residente te escriba primero, o configurá un template aprobado en Meta. Mientras, abrí WhatsApp manualmente.`,
              "warning"
            );
          } else {
            showToast(`❌ Meta rechazó el envío: ${reason || "razón desconocida"}`, "error");
          }
          return;
        }
      } catch (e) {
        console.warn("[WA polling intento", i, "fallo]", e);
      }
    }
    // Si después de N intentos sigue en "sent", asumimos OK silencioso.
  };

  const sendWhatsAppToResident = async ({ celular, nombre, incidentTitle, message, buildingId, incidentId }) => {
    const fullText = `Hola${nombre ? ` ${nombre}` : ""}\n\nTe escribimos desde Habitia sobre tu incidencia:\n"${incidentTitle}"\n\n${message}\n\n— Habitia`;

    // 1. Intento directo desde número Habitia. El edificio y la incidencia van
    // para que el servidor grabe buildingId en `messages` (C3) y la vigilancia
    // de entrega pueda leerlo. Con la incidencia, el servidor manda al celular
    // de quien la reportó (ignora el número del cliente), así que se llama
    // aunque acá no se haya podido leer la ficha.
    let directError = null;
    try {
      const result = await sendIncidentWhatsAppDirect(celular || "", fullText, { buildingId, incidentId });
      console.log("[WhatsApp directo OK]", result);
      return { ok: true, mode: "direct", messageId: result?.messageId };
    } catch (err) {
      directError = err?.message || String(err);
      console.warn("[WhatsApp directo falló]", { conCelular: !!celular, error: directError });
    }

    // Sin celular conocido no hay a qué número abrir wa.me.
    if (!celular) {
      return { ok: false, mode: "none", directError, sinCelular: true };
    }

    // 2. Fallback a wa.me (abre app/web del staff)
    try {
      const opened = await sendIncidentClientWhatsApp({
        nombre,
        celular,
        incidentTitle,
        message,
      });
      console.log("[Fallback wa.me]", { opened });
      return {
        ok: opened,
        mode: opened ? "wa_link" : "none",
        directError,
      };
    } catch (err) {
      console.error("[Fallback wa.me también falló]", err);
      return { ok: false, mode: "none", directError, fallbackError: err?.message };
    }
  };

  // Usar el filtro global del contexto
  const buildingFilter = selectedBuilding?.buildingid || (buildings && buildings.length === 1 ? buildings[0].buildingid : null);
  const sameBuildingId = (a, b) => {
    if (a == null || b == null) return false;
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb) && !Number.isNaN(na) && !Number.isNaN(nb)) {
      return na === nb;
    }
    return String(a).trim() === String(b).trim();
  };
  let buildingsToProcess = selectedBuilding
    ? buildings.filter((b) => sameBuildingId(b.buildingid, selectedBuilding.buildingid))
    : buildings;
  if (selectedBuilding && buildingsToProcess.length === 0) {
    buildingsToProcess = [selectedBuilding];
  }

  useEffect(() => {
    if (!buildings || buildings.length === 0) {
      return;
    }
    fetchIncidencias();
  }, [selectedBuilding, buildings]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterPriorityOpen && filterPriorityRef.current && !filterPriorityRef.current.contains(event.target)) {
        setFilterPriorityOpen(false);
      }
      if (filterTypeOpen && filterTypeRef.current && !filterTypeRef.current.contains(event.target)) {
        setFilterTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterPriorityOpen, filterTypeOpen]);

  const fetchIncidencias = async () => {
    try {
      setLoading(true);

      const fetchForBuildingId = async (bId) => {
        const buildingIn = tolerantId(bId);
        let q = query(
          collection(db, "incidents"),
          where("buildingid", "in", buildingIn),
          orderBy("date", "desc")
        );
        try {
          const snap = await getDocs(q);
          return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (_orderErr) {
          q = query(collection(db, "incidents"), where("buildingid", "in", buildingIn));
          const snap = await getDocs(q);
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => {
            const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || a.createdAt?.toDate?.() || 0);
            const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || b.createdAt?.toDate?.() || 0);
            return dateB - dateA;
          });
          return items;
        }
      };

      // BUG-64 fix: Paralelizar queries de incidencias por edificio
      const gathered = (await Promise.all(
        buildingsToProcess.map(b => fetchForBuildingId(b.buildingid))
      )).flat();

      setIncidencias(gathered);
    } catch (error) {
      console.error("Error al cargar incidencias:", error);
    } finally {
      setLoading(false);
    }
  };

  const getIssueIcon = (issue) => {
    const i = (issue || "").toString();
    switch (i) {
      case "Sanitario": return <FaBath size={12} />;
      case "Ascensor": return <FaArrowUp size={12} />;
      case "Electricidad": return <FaBolt size={12} />;
      case "Seguridad": return <FaShieldAlt size={12} />;
      case "Limpieza": return <FaBroom size={12} />;
      default: return <FaTag size={12} />;
    }
  };

  const getIssueLabel = (issue) => {
    return (issue || "Otros").toString();
  };

  const getSLALevel = (incidencia) => {
    if (normalizeIncidentStatus(incidencia.status || incidencia.state) === "Resuelta") return "resolved";
    const priority = (incidencia.priority || incidencia.prioridad || "").toString().toLowerCase();
    const config = SLA_CONFIG[priority] || SLA_CONFIG.media;
    const date = incidencia.date?.toDate ? incidencia.date.toDate() : new Date(incidencia.date || 0);
    const diffHours = (Date.now() - date.getTime()) / (1000 * 60 * 60);
    if (diffHours > config.criticalHours) return "critical";
    if (diffHours > config.warningHours) return "warning";
    return "ok";
  };

  const getSLADetail = (incidencia) => {
    const priority = (incidencia.priority || incidencia.prioridad || "").toString().toLowerCase();
    const config = SLA_CONFIG[priority] || SLA_CONFIG.media;
    const date = incidencia.date?.toDate ? incidencia.date.toDate() : new Date(incidencia.date || 0);
    const diffHours = (Date.now() - date.getTime()) / (1000 * 60 * 60);

    if (normalizeIncidentStatus(incidencia.status || incidencia.state) === "Resuelta") {
      // Calcular tiempo real de resolución
      const resolvedDate = incidencia.resolvedAt?.toDate
        ? incidencia.resolvedAt.toDate()
        : null;
      if (resolvedDate) {
        const resolutionHours = (resolvedDate.getTime() - date.getTime()) / (1000 * 60 * 60);
        const withinSLA = resolutionHours <= config.criticalHours;
        return {
          text: resolutionHours < 24
            ? `Resuelta en ${Math.round(resolutionHours)}h`
            : `Resuelta en ${Math.round(resolutionHours / 24)}d`,
          withinSLA,
        };
      }
      return { text: "Resuelta", withinSLA: true };
    }

    const remaining = config.criticalHours - diffHours;
    if (remaining <= 0) {
      const exceeded = Math.abs(remaining);
      return {
        text: exceeded < 24
          ? `SLA excedido por ${Math.round(exceeded)}h`
          : `SLA excedido por ${Math.round(exceeded / 24)}d`,
        withinSLA: false,
      };
    }
    return {
      text: remaining < 24
        ? `${Math.round(remaining)}h restantes`
        : `${Math.round(remaining / 24)}d restantes`,
      withinSLA: true,
    };
  };

  const getPrioridadStyle = (priority) => {
    const p = (priority || "").toString().toLowerCase();
    switch (p) {
      case "alta":
        return styles.priorityHigh;
      case "media":
        return styles.priorityMedium;
      case "baja":
        return styles.priorityLow;
      default:
        return styles.priorityMedium;
    }
  };

  const getEstadoStyle = (status) => {
    const c = normalizeIncidentStatus(status);
    switch (c) {
      case "Reportada":
        return styles.statusReported;
      case "En Proceso":
        return styles.statusInProgress;
      case "Resuelta":
        return styles.statusResolved;
      case "Pendiente":
        return styles.statusPending;
      default:
        return styles.statusDefault;
    }
  };

  const allowedTransitions = {
    "Reportada": ["En Proceso", "Pendiente", "Resuelta"],
    "En Proceso": ["Resuelta", "Pendiente"],
    "Pendiente": ["En Proceso", "Resuelta"],
    "Resuelta": [],
  };

  const handleStatusChange = (incidenciaId, newStatus) => {
    const incidencia = incidencias.find(inc => inc.id === incidenciaId);
    const oldStatus = normalizeIncidentStatus(incidencia?.status || incidencia?.state);
    const allowed = allowedTransitions[oldStatus] || [];
    if (!allowed.includes(newStatus)) {
      showToast(`No se puede cambiar de "${oldStatus}" a "${newStatus}".`, "warning");
      return;
    }
    // Solo guarda localmente; se aplica a Firestore al "Guardar y enviar"
    setPendingStatus(newStatus);
  };

  /**
   * Celular y nombre de quien reportó, para el saludo y el respaldo wa.me. El
   * envío directo no lo necesita: el servidor resuelve el destinatario con el
   * incidentId. Mismo criterio que el servidor (_celularDeQuienReporto): la
   * ficha vigente primero y el primer celular cargado. Con las reglas 0D (C10)
   * el staff lee fichas ajenas solo por accessBids del edificio
   * (fetchUsersByField). Best-effort: si no se puede leer, sin celular ni
   * nombre (nunca corta la respuesta).
   */
  const fetchReporterContact = async (inc, bid) => {
    const vacio = { celular: "", nombre: "" };
    const rawId = [inc?.reportedby, inc?.userid, inc?.reportedByEmployee].find((v) => v != null && v !== "");
    if (rawId == null) return vacio;
    let fichas = [];
    try {
      fichas = await fetchUsersByField("userid", tolerantId(rawId), bid);
    } catch (e) {
      console.warn("[Contacto de quien reportó] no se pudo leer la ficha:", e?.code || e?.message || e);
    }
    if (fichas.length === 0) {
      // Fichas viejas con el userid como id de documento (sin el campo).
      try {
        const byDoc = await getDoc(doc(db, "users", String(rawId)));
        if (byDoc.exists()) fichas = [byDoc.data() || {}];
      } catch (_) {
        /* la regla niega una ficha ajena sin accessBids o inexistente */
      }
    }
    const vigente = (u) => !u.archivedAt && u.mergedIntoUserid == null;
    const ordenadas = [...fichas].sort((a, b) => Number(!vigente(a)) - Number(!vigente(b)));
    if (ordenadas.length === 0) return vacio;
    return {
      celular: ordenadas.map((u) => String(u.celular || "").trim()).find(Boolean) || "",
      nombre: String(ordenadas[0].NombreCompleto || ordenadas[0].name || "").trim(),
    };
  };

  const handleResponseSubmit = async () => {
    const internal = (responseInternal || "").trim();
    const client = (responseClient || "").trim();
    if (!selectedIncidencia || (!internal && !client)) {
      showToast("Completá al menos una respuesta (interna o al cliente).", "warning");
      return;
    }

    try {
      setUpdating(true);

      const respAdmin = auth.currentUser;
      const now = new Date();
      const responseData = {
        messageInternal: internal || null,
        messageClient: client || null,
        message: client || internal || "",
        author: respAdmin?.email || "Administrador",
        authorId: respAdmin?.uid || "admin",
        date: now,
        incidenciaId: selectedIncidencia.id,
      };

      await addDoc(collection(db, "responses"), responseData);

      // C4: la nota interna va a incidentNotesInternal, que solo lee el staff
      // del edificio. El historial lo lee el residente: lleva solo la
      // respuesta al cliente, sin messageInternal.
      const incBuildingId = selectedIncidencia.buildingid ?? buildingFilter;
      // true si la nota quedó como antes de 0D (ver abajo).
      let notaEnHistorial = false;
      if (internal) {
        try {
          // Firmada con el email del token (o el uid): las reglas no aceptan otra.
          await addDoc(collection(db, "incidentNotesInternal"), {
            incidenciaId: selectedIncidencia.id,
            buildingid: incBuildingId,
            message: internal,
            createdAt: serverTimestamp(),
            createdBy: respAdmin?.email || respAdmin?.uid || "",
          });
        } catch (notaErr) {
          // Este cliente se despliega antes de publicar las reglas 0D, y las
          // vigentes no tienen incidentNotesInternal: mientras tanto la nota
          // queda como hasta ahora (historial marcado como interno y el último
          // texto en la incidencia). Con 0D esta rama no corre: el alta de
          // arriba pasa, y el historial ya no acepta messageInternal.
          if (!esPermisoDenegado(notaErr)) throw notaErr;
          notaEnHistorial = true;
          await addDoc(collection(db, "incidentHistory"), {
            incidenciaId: selectedIncidencia.id,
            action: "comment_added",
            message: internal,
            messageClient: null,
            messageInternal: internal,
            showToResident: false,
            author: respAdmin?.email || "Administrador",
            authorId: respAdmin?.uid || "admin",
            date: now,
            description: "Nota interna (solo administración)",
            timestamp: Date.now(),
          });
        }
      }
      if (client) {
        await addDoc(collection(db, "incidentHistory"), {
          incidenciaId: selectedIncidencia.id,
          action: "comment_added",
          message: client,
          messageClient: client,
          showToResident: true,
          author: respAdmin?.email || "Administrador",
          authorId: respAdmin?.uid || "admin",
          date: now,
          description: "Respuesta al cliente",
          timestamp: Date.now(),
        });
      }

      const incidenciaRef = doc(db, "incidents", selectedIncidencia.id);
      const prev = selectedIncidencia;
      const oldStatus = normalizeIncidentStatus(prev.status || prev.state);
      const newStatus = pendingStatus || oldStatus;
      const statusChanged = newStatus !== oldStatus;

      const patch = {
        hasResponse: true,
        hasClientResponse: !!(prev.hasClientResponse || client),
        updatedAt: serverTimestamp(),
      };
      if (statusChanged) {
        patch.status = newStatus;
        patch.state = newStatus;
        if (newStatus === "Resuelta") patch.resolvedAt = serverTimestamp();
      }
      if (internal) {
        // El texto de la nota no va al doc de la incidencia (lo lee el vecino
        // que la reportó): solo la fecha. Salvo con las reglas viejas (arriba).
        if (notaEnHistorial) patch.lastInternalResponse = internal;
        patch.lastInternalResponseDate = now;
      }
      if (client) {
        patch.lastClientResponse = client;
        patch.lastClientResponseDate = now;
        patch.lastResponse = client;
        patch.lastResponseDate = now;
      }
      await updateDoc(incidenciaRef, patch);

      if (statusChanged) {
        await addDoc(collection(db, "incidentHistory"), {
          incidenciaId: selectedIncidencia.id,
          action: "status_change",
          oldValue: oldStatus,
          newValue: newStatus,
          author: respAdmin?.email || "Administrador",
          authorId: respAdmin?.uid || "admin",
          date: now,
          description: `Estado cambiado de "${oldStatus}" a "${newStatus}" al enviar respuesta`,
          timestamp: Date.now(),
        });
      }

      if (client) {
        // El servidor resuelve el destinatario con el incidentId (el celular de
        // quien reportó), así que se envía aunque acá no se pueda leer la ficha
        // (C10). La ficha solo aporta el saludo y el número del respaldo wa.me.
        const { celular, nombre } = await fetchReporterContact(selectedIncidencia, incBuildingId);
        const result = await sendWhatsAppToResident({
          celular,
          nombre,
          incidentTitle: selectedIncidencia.title || "Incidencia",
          message: client,
          buildingId: incBuildingId,
          incidentId: selectedIncidencia.id,
        });
        const { ok, mode, directError } = result;
        const statusPrefix = statusChanged
          ? `Estado actualizado a "${formatStatusLabel(newStatus)}". `
          : "";
        if (ok && mode === "direct") {
          // Meta aceptó la solicitud (devolvió messageId). NO confirma
          // delivery — el webhook lo confirma 1-2s después.
          // Hacemos un polling rápido del doc en `messages` para
          // detectar si el delivery falla por error 131047 (residente
          // no escribió en 24h) y avisarle al staff con un toast nuevo.
          showToast(
            `${statusPrefix}WhatsApp aceptado por Meta — esperando confirmación de entrega...`,
            "success"
          );
          if (result.messageId) {
            pollWhatsAppDelivery(result.messageId, incBuildingId).catch((err) => {
              console.warn("[WA polling no concluyente]", err);
            });
          }
        } else if (ok && mode === "wa_link") {
          // ATENCIÓN: el mensaje todavía no llegó al residente. Se abrió
          // wa.me en otra pestaña; el staff DEBE apretar "Enviar" ahí.
          const motivoCorto = (directError || "").includes("24")
            ? "(conversación cerrada hace >24h)"
            : (directError || "").includes("no configurado") || (directError || "").includes("desplegada")
            ? "(WhatsApp Business no configurado todavía)"
            : "";
          showToast(
            `${statusPrefix}⚠️ WhatsApp directo no disponible ${motivoCorto}. Se abrió WhatsApp en otra pestaña: ANDÁ AHÍ Y APRETÁ ENVIAR para que el mensaje llegue al residente.`,
            "warning"
          );
        } else if (result.sinCelular && /celular/i.test(directError || "")) {
          // El servidor tampoco encontró un celular válido de quien reportó
          // (failed-precondition): no hay a quién mandarlo.
          showToast(
            statusChanged
              ? `Estado actualizado a "${formatStatusLabel(newStatus)}". No hay celular en el perfil del residente; la respuesta quedó guardada en Habitia.`
              : "Respuesta guardada en Habitia (el residente no tiene celular en su perfil).",
            "warning"
          );
        } else {
          // Ni directo ni wa.me funcionaron — mostrar motivo crudo para debug.
          const detail = directError || result.fallbackError || "motivo desconocido";
          console.error("[WA total fail]", { mode: result.mode, directError, fallbackError: result.fallbackError });
          showToast(
            `${statusPrefix}Respuesta guardada en Habitia. No se pudo enviar por WhatsApp (${detail}). Contactá al residente por otro medio o revisá la consola.`,
            "error"
          );
        }
      } else {
        showToast(
          statusChanged
            ? `Estado actualizado a "${formatStatusLabel(newStatus)}". Nota interna guardada.`
            : "Nota interna guardada (solo administración).",
          "success"
        );
      }

      // B1 — Si la respuesta al residente se envió, notificar a H3 para que
      // decida si guardarla como style_example (fire-and-forget).
      if (client) {
        recordReplyApproved(client).catch(() => {});
      }

      setResponseInternal("");
      setResponseClient("");
      setPendingStatus(null);
      setShowResponseModal(false);
      setSelectedIncidencia(null);
      setReplySuggestions(null);
      setSelectedSuggestionTone(null);
      setOriginalSuggestionText("");

      fetchIncidencias();
    } catch (error) {
      console.error("❌ Error al agregar respuesta:", error);
      showToast("Error al agregar la respuesta", "error");
    } finally {
      setUpdating(false);
    }
  };

  const getStats = () => {
    const total = incidencias.length;
    const reported = incidencias.filter(
      (inc) => normalizeIncidentStatus(inc.status || inc.state) === "Reportada"
    ).length;
    const inProgress = incidencias.filter(
      (inc) => normalizeIncidentStatus(inc.status || inc.state) === "En Proceso"
    ).length;
    const resolved = incidencias.filter((inc) => isIncidentResolved(inc)).length;
    
    return { total, reported, inProgress, resolved };
  };

  const getFilteredIncidencias = () => {
    let filtered = incidencias;

    // Filtrar por tab (abiertas/resueltas)
    if (activeTab === "abiertas") {
      filtered = filtered.filter((inc) => !isIncidentResolved(inc));
    } else if (activeTab === "resueltas") {
      filtered = filtered.filter((inc) => isIncidentResolved(inc));
    }

    // Filtrar por prioridad (los datos pueden venir en "alta"/" Alta" etc.)
    if (filterPriority !== "todas") {
      const priorityNorm = filterPriority.toLowerCase();
      filtered = filtered.filter(inc => {
        const incPriority = (inc.priority || inc.prioridad || "").toString().toLowerCase();
        return incPriority === priorityNorm;
      });
    }

    // Filtrar por tipo
    if (filterType !== "todas") {
      filtered = filtered.filter(inc => inc.type === filterType);
    }

    // Ordenar por prioridad (Alta > Media > Baja) y luego por fecha (más reciente primero)
    const priorityOrder = { alta: 0, media: 1, baja: 2 };
    filtered.sort((a, b) => {
      const pa = priorityOrder[(a.priority || a.prioridad || "").toString().toLowerCase()] ?? 1;
      const pb = priorityOrder[(b.priority || b.prioridad || "").toString().toLowerCase()] ?? 1;
      if (pa !== pb) return pa - pb;
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
      return dateB - dateA;
    });

    return filtered;
  };

  const getIncidentId = (id) => {
    if (!id) return "#INC-0000";
    const short = id.length > 8 ? id.slice(-6).toUpperCase() : id.toUpperCase();
    return `#INC-${short}`;
  };

  const formatRelativeTime = (date) => {
    if (!date) return "—";
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      const now = new Date();
      const diffMs = now - dateObj;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor(diffMs / (1000 * 60));
      if (diffMins < 60) return `Hace ${diffMins} min`;
      if (diffHours < 24) return `Hace ${diffHours} h`;
      if (diffDays === 1) return "Hace 1 día";
      if (diffDays < 7) return `Hace ${diffDays} días`;
      return dateObj.toLocaleDateString("es-UY", { day: "2-digit", month: "short" });
    } catch (error) {
      return "—";
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString("es-UY", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "—";
    }
  };

  const formatHistoryDate = (date) => {
    if (!date) return "—";
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString("es-UY", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      return "—";
    }
  };

  /**
   * Notas internas de una incidencia (C4): incidentNotesInternal, que solo lee
   * el staff del edificio. La consulta fija incidenciaId y el edificio.
   */
  const fetchInternalNotes = async (incidencia) => {
    const bid = incidencia?.buildingid ?? buildingFilter;
    if (!incidencia?.id || bid == null || bid === "") return [];
    const snap = await getDocs(
      query(
        collection(db, "incidentNotesInternal"),
        where("incidenciaId", "==", incidencia.id),
        where("buildingid", "in", tolerantId(bid))
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };
  const msDe = (ts) => (ts?.toMillis ? ts.toMillis() : ts?.toDate ? ts.toDate().getTime() : new Date(ts || 0).getTime());

  // Detalle: la última nota interna sale de incidentNotesInternal (las
  // incidencias viejas la tienen además en lastInternalResponse).
  useEffect(() => {
    if (!showDetailModal || !selectedIncidencia?.id) {
      setLastInternalNote(null);
      return undefined;
    }
    let vivo = true;
    fetchInternalNotes(selectedIncidencia)
      .then((notas) => {
        if (!vivo) return;
        const [ultima] = [...notas].sort((a, b) => msDe(b.createdAt) - msDe(a.createdAt));
        setLastInternalNote(ultima || null);
      })
      .catch((e) => {
        console.warn("No se pudieron cargar las notas internas:", e?.code || e?.message || e);
        if (vivo) setLastInternalNote(null);
      });
    return () => {
      vivo = false;
    };
    // Solo al abrir el detalle o cambiar de incidencia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailModal, selectedIncidencia?.id]);
  const notaInternaDetalle = lastInternalNote
    ? { texto: lastInternalNote.message, fecha: lastInternalNote.createdAt }
    : selectedIncidencia?.lastInternalResponse
      ? { texto: selectedIncidencia.lastInternalResponse, fecha: selectedIncidencia.lastInternalResponseDate }
      : null;

  const fetchIncidentHistory = async (incidencia) => {
    try {
      setLoadingHistory(true);
      const q = query(
        collection(db, "incidentHistory"),
        where("incidenciaId", "==", incidencia.id)
      );
      const [querySnapshot, notas] = await Promise.all([
        getDocs(q),
        fetchInternalNotes(incidencia).catch((e) => {
          console.warn("No se pudieron cargar las notas internas:", e?.code || e?.message || e);
          return [];
        }),
      ]);
      // Las notas internas se muestran en el registro como comentarios internos.
      const notasComoHistorial = notas.map((n) => ({
        id: `nota-${n.id}`,
        action: "comment_added",
        messageInternal: n.message,
        author: n.createdBy || "Administración",
        date: n.createdAt,
        timestamp: n.createdAt ? msDe(n.createdAt) : undefined,
        description: "Nota interna (solo administración)",
      }));
      const historyData = [
        ...querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
        ...notasComoHistorial,
      ]
        .sort((a, b) => {
          const ta = a.timestamp ?? (a.date?.toDate ? a.date.toDate().getTime() : new Date(a.date || 0).getTime());
          const tb = b.timestamp ?? (b.date?.toDate ? b.date.toDate().getTime() : new Date(b.date || 0).getTime());
          return ta - tb;
        });
      setIncidentHistory(historyData);
    } catch (error) {
      console.error("Error al cargar historial:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewHistory = (incidencia) => {
    setSelectedIncidencia(incidencia);
    setThreadSummary(null); // B6 — resetear resumen al abrir otra incidencia
    setShowHistoryModal(true);
    fetchIncidentHistory(incidencia);
  };


  if (!buildings || buildings.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><FaBuilding /></div>
          <h2 className={styles.emptyTitle}>No hay edificios asignados</h2>
          <p className={styles.emptyMessage}>
            No tienes edificios asignados para administrar.
          </p>
        </div>
      </div>
    );
  }

  // Mostrar mensaje de selección de edificio cuando no se ha seleccionado ninguno
  if (!buildingFilter || buildingFilter === "") {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><FaBuilding /></div>
          <h2 className={styles.emptyTitle}>Selecciona un Edificio</h2>
          <p className={styles.emptyMessage}>
            Para gestionar las incidencias del edificio, utiliza el filtro en la barra superior.
          </p>
        </div>
      </div>
    );
  }

  const stats = getStats();
  

  return (
    <div className={styles.container}>
      {/* Header unificado: título + tabs + filtros (estilo Proveedores/Gastos) */}
      <div className={styles.pageHeader}>
        <ScreenHeader icon={FaExclamationTriangle} title="Incidencias" subtitle="Gestión de incidencias" />
        <div className={styles.headerActions}>
          <div className={styles.headerSpacer} />
          {/* Tabs compactos */}
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewBtn} ${activeTab === "abiertas" ? styles.viewBtnActive : ""}`}
              onClick={() => setActiveTab("abiertas")}
              title="Incidencias abiertas"
            >
              <FaLockOpen size={14} />
              <span className={styles.viewBtnText}>Abiertas</span>
              <span className={styles.viewBtnCount}>{incidencias.filter((inc) => !isIncidentResolved(inc)).length}</span>
            </button>
            <button
              className={`${styles.viewBtn} ${activeTab === "resueltas" ? styles.viewBtnActive : ""}`}
              onClick={() => setActiveTab("resueltas")}
              title="Incidencias resueltas"
            >
              <FaCheckCircle size={14} />
              <span className={styles.viewBtnText}>Resueltas</span>
              <span className={styles.viewBtnCount}>{incidencias.filter((inc) => isIncidentResolved(inc)).length}</span>
            </button>
          </div>
          {/* Filtros compactos - mismo estilo que Proveedores */}
          <div className={styles.headerFilterSelect} ref={filterPriorityRef}>
            <FaFlag size={12} color={colors.primary} />
            <div className={styles.filterDropdownWrapper}>
              <button
                type="button"
                className={styles.filterDropdownTrigger}
                style={{ color: filterPriority !== "todas" ? colors.black : colors.gray400 }}
                onClick={() => {
                  setFilterPriorityOpen(!filterPriorityOpen);
                  setFilterTypeOpen(false);
                }}
              >
                {filterPriority === "todas" ? "Prioridad" : filterPriority}
              </button>
              <FaChevronDown size={10} color={colors.primary} className={styles.filterDropdownChevron} style={{ transform: filterPriorityOpen ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)" }} />
              {filterPriorityOpen && (
                <div className={styles.filterDropdown}>
                  <button type="button" className={`${styles.filterDropdownOption} incidencias-filter-option ${filterPriority === "todas" ? styles.filterDropdownOptionActive : ""}`} onClick={() => { setFilterPriority("todas"); setFilterPriorityOpen(false); }}>Prioridad</button>
                  <button type="button" className={`${styles.filterDropdownOption} incidencias-filter-option ${filterPriority === "Alta" ? styles.filterDropdownOptionActive : ""}`} onClick={() => { setFilterPriority("Alta"); setFilterPriorityOpen(false); }}>Alta</button>
                  <button type="button" className={`${styles.filterDropdownOption} incidencias-filter-option ${filterPriority === "Media" ? styles.filterDropdownOptionActive : ""}`} onClick={() => { setFilterPriority("Media"); setFilterPriorityOpen(false); }}>Media</button>
                  <button type="button" className={`${styles.filterDropdownOption} incidencias-filter-option ${filterPriority === "Baja" ? styles.filterDropdownOptionActive : ""}`} onClick={() => { setFilterPriority("Baja"); setFilterPriorityOpen(false); }}>Baja</button>
                </div>
              )}
            </div>
          </div>
          <div className={styles.headerFilterSelect} ref={filterTypeRef}>
            <FaList size={12} color={colors.primary} />
            <div className={styles.filterDropdownWrapper}>
              <button
                type="button"
                className={styles.filterDropdownTrigger}
                style={{ color: filterType !== "todas" ? colors.black : colors.gray400 }}
                onClick={() => {
                  setFilterTypeOpen(!filterTypeOpen);
                  setFilterPriorityOpen(false);
                }}
              >
                {filterType === "todas" ? "Tipo" : filterType}
              </button>
              <FaChevronDown size={10} color={colors.primary} className={styles.filterDropdownChevron} style={{ transform: filterTypeOpen ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)" }} />
              {filterTypeOpen && (
                <div className={styles.filterDropdown}>
                  <button type="button" className={`${styles.filterDropdownOption} incidencias-filter-option ${filterType === "todas" ? styles.filterDropdownOptionActive : ""}`} onClick={() => { setFilterType("todas"); setFilterTypeOpen(false); }}>Tipo</button>
                  <button type="button" className={`${styles.filterDropdownOption} incidencias-filter-option ${filterType === "Personal" ? styles.filterDropdownOptionActive : ""}`} onClick={() => { setFilterType("Personal"); setFilterTypeOpen(false); }}>Personal</button>
                  <button type="button" className={`${styles.filterDropdownOption} incidencias-filter-option ${filterType === "Edificio" ? styles.filterDropdownOptionActive : ""}`} onClick={() => { setFilterType("Edificio"); setFilterTypeOpen(false); }}>Edificio</button>
                </div>
              )}
            </div>
          </div>
          {(filterPriority !== "todas" || filterType !== "todas") && (
            <button
              type="button"
              className={styles.clearFiltersIconBtn}
              onClick={() => {
                setFilterPriority("todas");
                setFilterType("todas");
              }}
              title="Limpiar filtros"
            >
              <FaEraser size={14} color={colors.gray600} />
            </button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        {/* Incidencias List */}
        <div className={styles.incidenciasList}>
          <div className={styles.listHeader}>
            <div className={styles.listTitleSection}>
              <div className={styles.listIcon}>
                {activeTab === "abiertas" ? <FaLockOpen /> : <FaCheckCircle />}
              </div>
              <div className={styles.listTitleContent}>
                <h2 className={styles.listTitle}>
                  Incidencias {activeTab === "abiertas" ? "Abiertas" : "Resueltas"}
                </h2>
                <p className={styles.listSubtitle}>
                  {getFilteredIncidencias().length} incidencias encontradas
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className={styles.incidenciasGrid}>
              {[1, 2, 3].map((i) => (
                <div key={i} className={styles.incidenciaCardSkeleton}>
                  <div className={styles.skeletonHeader} />
                  <div className={styles.skeletonIssue} />
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} style={{ width: "70%" }} />
                  <div className={styles.skeletonMeta} />
                  <div className={styles.skeletonFooter} />
                </div>
              ))}
            </div>
          ) : getFilteredIncidencias().length === 0 ? (
            (filterPriority !== "todas" || filterType !== "todas") ? (
              <div style={{ textAlign: "center", padding: "32px 24px", color: colors.gray600 }}>
                <p style={{ fontSize: typography.sizes.md }}>No se encontraron incidencias con el filtro seleccionado.</p>
                <button onClick={() => { setFilterPriority("todas"); setFilterType("todas"); }} style={{ marginTop: 8, padding: "6px 16px", borderRadius: 6, border: `1px solid ${colors.gray200}`, background: colors.white, cursor: "pointer", fontSize: typography.sizes.base, color: colors.primary }}>Limpiar filtros</button>
              </div>
            ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                {activeTab === "abiertas" ? <FaLockOpen size={48} /> : <FaCheckCircle size={48} />}
              </div>
              <h2 className={styles.emptyTitle}>
                No hay incidencias {activeTab === "abiertas" ? "abiertas" : "resueltas"}
              </h2>
              <p className={styles.emptyMessage}>
                {activeTab === "abiertas"
                  ? "No se encontraron incidencias abiertas con los filtros aplicados."
                  : "No se encontraron incidencias resueltas con los filtros aplicados."
                }
              </p>
            </div>
            )
          ) : (
            <div className={styles.incidenciasGrid}>
              {getFilteredIncidencias().map((incidencia) => (
                <div
                  key={incidencia.id}
                  className={styles.incidenciaCard}
                  data-status={normalizeIncidentStatus(incidencia.status || incidencia.state)}
                  data-priority={(incidencia.priority || incidencia.prioridad || "").toString().toLowerCase().replace(/^./, c => c.toUpperCase())}
                  data-sla={getSLALevel(incidencia)}
                  onClick={() => {
                    setSelectedIncidencia(incidencia);
                    setImageDiagnosis(null); // D2 — limpiar diagnostico anterior al abrir otra incidencia
                    setShowDetailModal(true);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Ver detalles de incidencia ${getIncidentId(incidencia.id)}: ${incidencia.title}`}
                  onKeyDown={(e) => e.key === "Enter" && (
                    setSelectedIncidencia(incidencia),
                    setImageDiagnosis(null),
                    setShowDetailModal(true)
                  )}
                >
                  {/* Encabezado: ID + prioridad + estado */}
                  <div className={styles.incidenciaHeader}>
                    <span className={styles.incidenciaId}>{getIncidentId(incidencia.id)}</span>
                    <div className={styles.incidenciaMeta}>
                      <span className={`${styles.priorityBadge} ${getPrioridadStyle(incidencia.priority || incidencia.prioridad)}`}>
                        {(incidencia.priority || incidencia.prioridad || "—").toString().replace(/^./, c => c.toUpperCase())}
                      </span>
                      <span className={`${styles.statusBadge} ${getEstadoStyle(incidencia.status || incidencia.state)}`}>
                        {formatStatusLabel(normalizeIncidentStatus(incidencia.status || incidencia.state))}
                      </span>
                    </div>
                  </div>
                  {/* Tipo de incidencia (issue) con icono distintivo */}
                  <div className={styles.incidenciaIssue}>
                    <span className={styles.issueIcon}>{getIssueIcon(incidencia.issue)}</span>
                    <span className={styles.issueText}>{getIssueLabel(incidencia.issue)}</span>
                  </div>

                  {/* Cuerpo: título, descripción, metadatos, evidencia */}
                  <div className={styles.incidenciaContent}>
                    <h3 className={styles.incidenciaTitle}>{incidencia.title || "Sin título"}</h3>
                    <p className={styles.incidenciaDescription}>{incidencia.description || "—"}</p>
                    <div className={styles.incidenciaDetails}>
                      {incidencia.ubication && (
                        <div className={styles.incidenciaDetail}>
                          <FaBuilding size={12} color={colors.primary} />
                          <span>{incidencia.ubication}</span>
                        </div>
                      )}
                      <div className={styles.incidenciaDetail}>
                        <FaClock size={12} color={colors.primary} />
                        <span>{formatRelativeTime(incidencia.date)}</span>
                      </div>
                    </div>
                    {incidencia.imageUrl && (
                      <div className={styles.incidenciaThumbnail}>
                        <img src={incidencia.imageUrl} alt="Evidencia" loading="lazy" />
                      </div>
                    )}
                  </div>

                  {/* Pie: indicador SLA + acción prioritaria */}
                  <div className={styles.incidenciaFooter}>
                    <span className={`${styles.slaIndicator} ${styles[`sla${getSLALevel(incidencia).charAt(0).toUpperCase() + getSLALevel(incidencia).slice(1)}`]}`} title={getSLADetail(incidencia).text}>
                      {getSLALevel(incidencia) === "resolved" ? <FaCheckCircle size={12} /> : <FaClock size={12} />}
                      <span>{getSLADetail(incidencia).text}</span>
                    </span>
                    <span className={styles.incidenciaFooterHint}>
                      {!isIncidentResolved(incidencia)
                        ? "Responder para cerrar (interno y/o al cliente)"
                        : "Ver detalles y gestionar"}
                    </span>
                  </div>
                </div>
              ))}
        </div>
          )}
        </div>
      </div>

      {/* Modal Detalle de Incidencia */}
      {showDetailModal && selectedIncidencia && (
        <div className={styles.detailModal} onClick={() => { setShowDetailModal(false); setPendingStatus(null); setImageDiagnosis(null); }}>
          <div className={styles.detailModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.detailModalHeader}>
              <div>
                <span className={styles.detailModalId}>{getIncidentId(selectedIncidencia.id)}</span>
                <h3 className={styles.detailModalTitle}>{selectedIncidencia.title}</h3>
              </div>
              <button type="button" className={styles.detailModalClose} onClick={() => { setShowDetailModal(false); setPendingStatus(null); setImageDiagnosis(null); }} aria-label="Cerrar">×</button>
            </div>
            <div className={styles.detailModalMeta}>
              <span className={`${styles.priorityBadge} ${getPrioridadStyle(selectedIncidencia.priority || selectedIncidencia.prioridad)}`}>
                {(selectedIncidencia.priority || selectedIncidencia.prioridad || "—").toString().replace(/^./, c => c.toUpperCase())}
              </span>
              <span className={`${styles.statusBadge} ${getEstadoStyle(selectedIncidencia.status || selectedIncidencia.state)}`}>
                {formatStatusLabel(normalizeIncidentStatus(selectedIncidencia.status || selectedIncidencia.state))}
              </span>
              <span className={styles.detailModalIssue}>
                {getIssueIcon(selectedIncidencia.issue)}
                {getIssueLabel(selectedIncidencia.issue)}
              </span>
              <span className={styles.detailModalType}>{selectedIncidencia.type === "Personal" ? <><FaUser size={10} /> Personal</> : <><FaBuilding size={10} /> Edificio</>}</span>
            </div>

            {/* D1 — Sticker de triage automático (Fase 2). Si la incidencia
                tiene ai_triage cacheado, lo mostramos como banner discreto
                arriba del cuerpo. No reemplaza priority/type oficiales —
                es una segunda lectura de la IA visible para el staff. */}
            {(() => {
              const t = selectedIncidencia.ai_triage || selectedIncidencia.aiTriage;
              if (!t) return null;
              const sevColor =
                t.priority === "critica" ? colors.error :
                t.priority === "alta" ? colors.warningDark || colors.warning :
                t.priority === "media" ? colors.primary : colors.gray600;
              const sevBg =
                t.priority === "critica" ? colors.errorLight :
                t.priority === "alta" ? colors.warningLight :
                t.priority === "media" ? colors.primaryLighter : colors.gray100;
              return (
                <div style={{
                  margin: "0 0 12px",
                  padding: "10px 14px",
                  background: sevBg,
                  borderRadius: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ fontSize: typography.sizes.sm, color: sevColor, fontWeight: 400 }}>
                      🤖 Triage IA
                    </strong>
                    <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.white, color: sevColor, textTransform: "capitalize" }}>
                      {t.priority || "—"}
                    </span>
                    <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.white, color: colors.gray700, textTransform: "capitalize" }}>
                      {(t.type || "otros").replace("_", " ")}
                    </span>
                    {typeof t.urgency_hours === "number" && (
                      <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.white, color: colors.gray700 }}>
                        {t.urgency_hours}h SLA
                      </span>
                    )}
                    {t.suggested_provider_name && (
                      <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.white, color: colors.gray700 }}>
                        Proveedor: {t.suggested_provider_name}
                      </span>
                    )}
                    {typeof t.confidence === "number" && (
                      <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.white, color: t.confidence >= 0.7 ? colors.success : colors.gray600 }}>
                        {Math.round(t.confidence * 100)}% conf.
                      </span>
                    )}
                    {t.alert_staff && (
                      <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.error, color: colors.white }}>
                        ⚠ Acción urgente
                      </span>
                    )}
                  </div>
                  {t.reasoning && (
                    <div style={{ fontSize: 12, color: colors.gray700, lineHeight: 1.45 }}>
                      {t.reasoning}
                    </div>
                  )}
                  {t.recommended_user_message && (
                    <div style={{ fontSize: 12, color: colors.gray900, padding: "6px 10px", background: colors.white, borderRadius: 6, fontStyle: "italic" }}>
                      <div style={{ fontSize: 11, color: colors.gray600, marginBottom: 2, fontStyle: "normal" }}>Mensaje recomendado al residente:</div>
                      {t.recommended_user_message}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className={styles.detailModalBody}>
              <p className={styles.detailModalDescription}>{selectedIncidencia.description || "—"}</p>
              {selectedIncidencia.ubication && (
                <div className={styles.detailModalDetail}><FaBuilding size={14} color={colors.primary} /> {selectedIncidencia.ubication}</div>
              )}
              <div className={styles.detailModalDetail}><FaClock size={14} color={colors.primary} /> {formatDate(selectedIncidencia.date)}</div>
              {selectedIncidencia.imageUrl && (
                <>
                  <div className={styles.detailModalImage}><img src={selectedIncidencia.imageUrl} alt="Evidencia" loading="lazy" /></div>
                  {/* D2 — Diagnóstico visual con IA */}
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    background: imageDiagnosis ? colors.primaryLighter : colors.gray50,
                    borderRadius: 8,
                    border: `1px solid ${imageDiagnosis ? colors.primaryLight : colors.gray200}`,
                  }}>
                    {!imageDiagnosis && !diagnosingImage && (
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <span style={{ fontSize: typography.sizes.sm, color: colors.gray700 }}>
                          ¿Querés un diagnóstico técnico rápido de la foto?
                        </span>
                        <button
                          type="button"
                          onClick={handleDiagnoseImage}
                          style={{
                            padding: "6px 14px",
                            fontSize: 13,
                            color: colors.primary,
                            background: colors.white,
                            border: `1px solid ${colors.primaryLight}`,
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        >
                          🔍 Diagnosticar con IA
                        </button>
                      </div>
                    )}
                    {diagnosingImage && (
                      <div style={{ fontSize: 13, color: colors.gray600 }}>Analizando imagen…</div>
                    )}
                    {imageDiagnosis && (
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, alignItems: "center" }}>
                          <strong style={{ fontSize: typography.sizes.sm, color: colors.primary }}>
                            🔍 Diagnóstico (IA)
                          </strong>
                          <button
                            type="button"
                            onClick={() => setImageDiagnosis(null)}
                            style={{ background: "transparent", border: "none", color: colors.gray600, cursor: "pointer", fontSize: 12 }}
                          >
                            Cerrar
                          </button>
                        </div>
                        <div style={{ fontSize: 13, color: colors.gray900, marginBottom: 8, lineHeight: 1.5 }}>
                          {imageDiagnosis.problem_description || "(sin descripción)"}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                          {imageDiagnosis.severity && (
                            <span style={{
                              padding: "2px 10px",
                              fontSize: 11,
                              borderRadius: 999,
                              background: imageDiagnosis.severity === "critica" ? colors.errorLight
                                : imageDiagnosis.severity === "grave" ? colors.warningLight
                                : imageDiagnosis.severity === "moderada" ? colors.primaryLighter
                                : colors.gray100,
                              color: imageDiagnosis.severity === "critica" ? colors.error
                                : imageDiagnosis.severity === "grave" ? colors.warningDark
                                : imageDiagnosis.severity === "moderada" ? colors.primary
                                : colors.gray700,
                            }}>
                              Gravedad: {imageDiagnosis.severity}
                            </span>
                          )}
                          {imageDiagnosis.suggested_specialty && (
                            <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.gray100, color: colors.gray700 }}>
                              Necesita: {imageDiagnosis.suggested_specialty}
                            </span>
                          )}
                          {imageDiagnosis.urgency_estimate && (
                            <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 999, background: colors.gray100, color: colors.gray700 }}>
                              Urgencia: {imageDiagnosis.urgency_estimate}
                            </span>
                          )}
                          {imageDiagnosis.confidence !== undefined && (
                            <span style={{
                              padding: "2px 10px",
                              fontSize: 11,
                              borderRadius: 999,
                              background: imageDiagnosis.confidence >= 0.7 ? colors.successLight : colors.warningLight,
                              color: imageDiagnosis.confidence >= 0.7 ? colors.success : colors.warningDark,
                            }}>
                              {Math.round(imageDiagnosis.confidence * 100)}% confianza
                            </span>
                          )}
                        </div>
                        {imageDiagnosis.immediate_actions && imageDiagnosis.immediate_actions.length > 0 && (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 4 }}>Acciones inmediatas:</div>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: colors.gray900 }}>
                              {imageDiagnosis.immediate_actions.map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
              {(notaInternaDetalle || selectedIncidencia.lastClientResponse || selectedIncidencia.lastResponse) && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.gray200}`, display: "flex", flexDirection: "column", gap: 12 }}>
                  {notaInternaDetalle && (
                    <div>
                      <div style={{ fontSize: typography.sizes.xs, fontWeight: 400, color: colors.warningDark, marginBottom: 4 }}>Nota interna (solo administración)</div>
                      <p style={{ margin: 0, fontSize: typography.sizes.md, color: colors.gray900, whiteSpace: "pre-wrap" }}>{notaInternaDetalle.texto}</p>
                      {notaInternaDetalle.fecha && (
                        <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: 4 }}>{formatDate(notaInternaDetalle.fecha)}</div>
                      )}
                    </div>
                  )}
                  {(selectedIncidencia.lastClientResponse || selectedIncidencia.lastResponse) && (
                    <div>
                      <div style={{ fontSize: typography.sizes.xs, fontWeight: 400, color: colors.successDark, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                        <FaWhatsapp size={12} color="#25D366" /> Respuesta al residente
                      </div>
                      <p style={{ margin: 0, fontSize: typography.sizes.md, color: colors.gray900, whiteSpace: "pre-wrap" }}>
                        {selectedIncidencia.lastClientResponse || selectedIncidencia.lastResponse}
                      </p>
                      {(selectedIncidencia.lastClientResponseDate || selectedIncidencia.lastResponseDate) && (
                        <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: 4 }}>
                          {formatDate(selectedIncidencia.lastClientResponseDate || selectedIncidencia.lastResponseDate)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.detailModalFooter}>
              <select
                className={styles.stateSelect}
                value={pendingStatus || normalizeIncidentStatus(selectedIncidencia.status || selectedIncidencia.state)}
                onChange={(e) => handleStatusChange(selectedIncidencia.id, e.target.value)}
                disabled={
                  (allowedTransitions[
                    normalizeIncidentStatus(selectedIncidencia.status || selectedIncidencia.state)
                  ] || []).length === 0
                }
                onClick={(e) => e.stopPropagation()}
                style={pendingStatus ? { outline: `2px solid ${colors.warning}`, borderRadius: 6 } : {}}
                title={pendingStatus ? "Estado pendiente — se guardará al enviar respuesta" : ""}
              >
                {(() => {
                  const c = normalizeIncidentStatus(selectedIncidencia.status || selectedIncidencia.state);
                  const next = allowedTransitions[c] || [];
                  return (
                    <>
                      <option value={c}>{formatStatusLabel(c)}</option>
                      {next.map((s) => (
                        <option key={s} value={s}>
                          {formatStatusLabel(s)}
                        </option>
                      ))}
                    </>
                  );
                })()}
              </select>
              {pendingStatus && (
                <span style={{ fontSize: typography.sizes.xs, color: colors.warning, fontStyle: "italic" }}>
                  Nuevo estado al guardar: <strong>{formatStatusLabel(pendingStatus)}</strong>
                </span>
              )}
              <button type="button" className={styles.historyButton} onClick={(e) => { e.stopPropagation(); handleViewHistory(selectedIncidencia); setShowDetailModal(false); }}>Ver Historial</button>
              <button type="button" className={styles.responseButton} onClick={(e) => { e.stopPropagation(); setShowDetailModal(false); setShowResponseModal(true); }}>Responder</button>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedIncidencia && (
        <div className={styles.responseModal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>Responder incidencia</h3>
                <p style={{ margin: "6px 0 0", fontSize: typography.sizes.base, color: colors.gray600 }}>
                  {getIncidentId(selectedIncidencia.id)} · {selectedIncidencia.title || "Sin título"}
                </p>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => {
                  setShowResponseModal(false);
                  setSelectedIncidencia(null);
                  setResponseInternal("");
                  setResponseClient("");
                  setPendingStatus(null);
                  setReplySuggestions(null);
                  setSelectedSuggestionTone(null);
                  setOriginalSuggestionText("");
                }}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div className={styles.responseForm}>
              <label className={styles.responseLabel}>Nota interna (solo administración del edificio)</label>
              <p style={{ margin: "0 0 8px", fontSize: typography.sizes.sm, color: colors.gray600 }}>
                Podés usar solo esta nota, solo la respuesta al residente, o ambas. Si la incidencia aún no está resuelta, al guardar pasa a <strong>Resuelta</strong> (también podés elegir <strong>Resuelta</strong> antes en el combo de estado).
              </p>
              <textarea
                className={styles.responseTextarea}
                value={responseInternal}
                onChange={(e) => setResponseInternal(e.target.value)}
                placeholder="Coordinación interna, proveedores, detalles que no ves el residente…"
                rows={3}
              />

              <label className={styles.responseLabel} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                <FaWhatsapp color="#25D366" size={14} />
                Respuesta al residente
              </label>
              <p style={{ margin: "0 0 8px", fontSize: typography.sizes.sm, color: colors.gray600 }}>
                Si la incidencia aún no está cerrada, al guardar también pasa a <strong>Resuelta</strong>. Si completás este campo, se envía automáticamente por WhatsApp al residente (si tiene celular en su perfil).
              </p>

              {/* B1 — Respuestas sugeridas por IA */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                {!replySuggestions && !loadingSuggestions && (
                  <button
                    type="button"
                    onClick={handleFetchSuggestions}
                    aria-label="Generar respuestas sugeridas con IA"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      fontSize: typography.sizes.sm,
                      fontWeight: 400,
                      color: colors.primary,
                      background: colors.primaryLighter,
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    ✨ Sugerir respuestas
                  </button>
                )}
                {loadingSuggestions && (
                  <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
                    Generando 3 borradores con el estilo aprendido del staff…
                  </span>
                )}
                {replySuggestions?.suggestions?.length > 0 && (
                  <>
                    <span style={{ fontSize: typography.sizes.xs, color: colors.gray600, marginRight: 4 }}>
                      Sugerencias:
                    </span>
                    {replySuggestions.suggestions
                      .filter((s) => s.text)
                      .filter((s) =>
                        s.tone !== "firme" || replySuggestions.firm_tone_recommended
                      )
                      .map((s) => {
                        const isActive = selectedSuggestionTone === s.tone;
                        return (
                          <button
                            key={s.tone}
                            type="button"
                            onClick={() => applySuggestion(s)}
                            aria-label={`Aplicar tono ${s.tone}`}
                            title={s.text}
                            style={{
                              padding: "6px 12px",
                              fontSize: typography.sizes.sm,
                              fontWeight: 400,
                              color: isActive ? "#fff" : colors.primary,
                              background: isActive ? colors.primary : colors.primaryLighter,
                              border: "none",
                              borderRadius: 9999,
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              textTransform: "capitalize",
                            }}
                          >
                            {s.tone}
                          </button>
                        );
                      })}
                    <button
                      type="button"
                      onClick={() => {
                        setReplySuggestions(null);
                        setSelectedSuggestionTone(null);
                        setOriginalSuggestionText("");
                      }}
                      aria-label="Descartar sugerencias"
                      style={{
                        padding: "6px 10px",
                        fontSize: typography.sizes.xs,
                        color: colors.gray600,
                        background: "transparent",
                        border: "none",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                    >
                      Limpiar
                    </button>
                  </>
                )}
              </div>

              <textarea
                className={styles.responseTextarea}
                value={responseClient}
                onChange={(e) => setResponseClient(e.target.value)}
                placeholder="Mensaje visible para el residente en Habitia y enviado por WhatsApp…"
                rows={4}
              />

              <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleImproveWithAI}
                  disabled={improvingAI || !responseClient.trim()}
                  aria-label="Mejorar redacción con IA"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 16px",
                    fontSize: typography.sizes.sm,
                    fontWeight: 400,
                    color: improvingAI || !responseClient.trim() ? colors.gray400 : colors.primary,
                    background: colors.primaryLighter,
                    border: "none",
                    borderRadius: 8,
                    cursor: improvingAI || !responseClient.trim() ? "not-allowed" : "pointer",
                    transition: "all 0.15s ease",
                    opacity: improvingAI || !responseClient.trim() ? 0.6 : 1,
                  }}
                >
                  {improvingAI ? "Mejorando…" : "✨ Mejorar con IA"}
                </button>
                <span style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>
                  Reescribe tu borrador en español rioplatense, profesional y cordial.
                </span>
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "16px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className={styles.submitResponseButton}
                  onClick={handleResponseSubmit}
                  disabled={(!responseInternal.trim() && !responseClient.trim()) || updating}
                >
                  {updating ? "Guardando..." : "Guardar y enviar"}
                </button>
                <button
                  type="button"
                  className={styles.cancelResponseButton}
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedIncidencia(null);
                    setResponseInternal("");
                    setResponseClient("");
                    setPendingStatus(null);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedIncidencia && (
        <div className={styles.historyModal}>
          <div className={styles.historyModalContent}>
            <div className={styles.historyModalHeader}>
              <div className={styles.historyModalTitleSection}>
                <div className={styles.historyModalIcon}><FaHistory size={24} /></div>
                <div className={styles.historyModalTitleContent}>
                  <h3 className={styles.historyModalTitle}>
                    Historial de Incidencia
                  </h3>
                  <p className={styles.historyModalSubtitle}>
                    {selectedIncidencia.title}
                  </p>
                </div>
              </div>
              <button
                className={styles.historyModalClose}
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedIncidencia(null);
                  setIncidentHistory([]);
                  setThreadSummary(null);
                }}
                aria-label="Cerrar"
              >
                <FaTimes />
              </button>
            </div>

            {/* B6 — Resumen del hilo con IA. Solo se ofrece si hay >=3 eventos
                porque para hilos chicos no aporta. */}
            {incidentHistory.length >= 3 && (
              <div style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${colors.gray200}`,
                background: threadSummary ? colors.primaryLighter : colors.gray50,
              }}>
                {!threadSummary ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: typography.sizes.sm, color: colors.gray700 }}>
                      Este hilo tiene {incidentHistory.length + 1} eventos. ¿Querés un resumen rápido?
                    </span>
                    <button
                      type="button"
                      onClick={handleSummarizeThread}
                      disabled={summarizingThread}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 14px",
                        fontSize: typography.sizes.sm,
                        fontWeight: 400,
                        color: colors.primary,
                        background: colors.white,
                        border: `1px solid ${colors.primaryLight}`,
                        borderRadius: 8,
                        cursor: summarizingThread ? "not-allowed" : "pointer",
                        opacity: summarizingThread ? 0.6 : 1,
                      }}
                    >
                      {summarizingThread ? "Resumiendo…" : "📋 Resumir hilo con IA"}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <strong style={{ fontSize: typography.sizes.sm, color: colors.primary }}>
                        📋 Resumen del hilo (IA)
                      </strong>
                      <button
                        type="button"
                        onClick={() => setThreadSummary(null)}
                        aria-label="Cerrar resumen"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.gray600,
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Cerrar
                      </button>
                    </div>
                    {threadSummary.summary?.estado_actual && (
                      <div style={{ marginBottom: 8, fontSize: 13, color: colors.gray900 }}>
                        <strong>Estado actual:</strong> {threadSummary.summary.estado_actual}
                      </div>
                    )}
                    {threadSummary.summary?.cronologia?.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 4 }}>Cronología:</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: colors.gray900 }}>
                          {threadSummary.summary.cronologia.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                      </div>
                    )}
                    {threadSummary.summary?.decisiones?.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 4 }}>Decisiones:</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: colors.gray900 }}>
                          {threadSummary.summary.decisiones.map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                      </div>
                    )}
                    {threadSummary.summary?.proximo_paso && (
                      <div style={{ marginBottom: 8, fontSize: 13, color: colors.gray900 }}>
                        <strong>Próximo paso:</strong> {threadSummary.summary.proximo_paso}
                      </div>
                    )}
                    {threadSummary.summary?.bloqueos?.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        <div style={{ fontSize: 12, color: colors.error, marginBottom: 4 }}>⚠️ Bloqueos:</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: colors.gray900 }}>
                          {threadSummary.summary.bloqueos.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className={styles.historyContent}>
              {loadingHistory ? (
                <div className={styles.historyLoading}>
                  <div className={styles.historyLoadingSpinner}></div>
                  <span>Cargando historial...</span>
                </div>
              ) : incidentHistory.length === 0 ? (
                <div className={styles.historyEmpty}>
                  <div className={styles.historyEmptyIcon}><FaClipboardList size={32} /></div>
                  <h4 className={styles.historyEmptyTitle}>Sin historial</h4>
                  <p className={styles.historyEmptyText}>
                    No hay registros de actividad para esta incidencia.
                  </p>
                </div>
              ) : (
                <div className={styles.historyContainer}>
                  <div className={styles.historySummary}>
                    <div className={styles.historySummaryItem}>
                      <span className={styles.historySummaryIcon}><FaClock size={14} /></span>
                      <span className={styles.historySummaryLabel}>Creada:</span>
                      <span className={styles.historySummaryValue}>
                        {formatHistoryDate(selectedIncidencia.date)}
                      </span>
                    </div>
                    <div className={styles.historySummaryItem}>
                      <span className={styles.historySummaryIcon}><FaClipboardList size={14} /></span>
                      <span className={styles.historySummaryLabel}>Actividades:</span>
                      <span className={styles.historySummaryValue}>
                        {incidentHistory.length + 1} registros
                      </span>
                    </div>
                  </div>

                  <div className={styles.historyTableContainer}>
                    <div className={styles.historyTableHeader}>
                      <h4 className={styles.historyTableTitle}>Registro de Actividades</h4>
                      <div className={styles.historyTableBadge}>
                        {incidentHistory.length + 1} eventos
                      </div>
                    </div>
                    
                    <table className={styles.historyTable}>
                      <thead>
                        <tr>
                          <th className={styles.historyThDate}>Fecha y Hora</th>
                          <th className={styles.historyThAction}>Acción</th>
                          <th className={styles.historyThUser}>Usuario</th>
                          <th className={styles.historyThDescription}>Descripción</th>
                          <th className={styles.historyThDetails}>Detalles</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Creación de la incidencia */}
                        <tr className={styles.historyRowCreated}>
                          <td className={styles.historyDate}>
                            <div className={styles.historyDateTime}>
                              <span className={styles.historyDateText}>
                                {formatHistoryDate(selectedIncidencia.date)}
                              </span>
                            </div>
                          </td>
                          <td className={styles.historyAction}>
                            <div className={styles.historyActionBadge}>
                              <span className={styles.historyActionIcon}><FaClipboardList size={12} /></span>
                              <span>Creación</span>
                            </div>
                          </td>
                          <td className={styles.historyUser}>
                            <div className={styles.historyUserBadge}>
                              <span className={styles.historyUserIcon}><FaUser size={12} /></span>
                              <span>Usuario</span>
                            </div>
                          </td>
                          <td className={styles.historyDescription}>
                            Incidencia reportada por el usuario
                          </td>
                          <td className={styles.historyDetails}>
                            <div className={styles.historyDetailsContent}>
                              <span className={styles.historyDetailsTitle}>
                                {selectedIncidencia.title}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {/* Historial de cambios */}
                        {incidentHistory.map((historyItem, index) => (
                          <tr 
                            key={historyItem.id} 
                            className={`${styles.historyRow} ${
                              historyItem.action === "status_change" 
                                ? styles.historyRowStatus 
                                : styles.historyRowComment
                            }`}
                          >
                            <td className={styles.historyDate}>
                              <div className={styles.historyDateTime}>
                                <span className={styles.historyDateText}>
                                  {formatHistoryDate(historyItem.date)}
                                </span>
                              </div>
                            </td>
                            <td className={styles.historyAction}>
                              <div className={`${styles.historyActionBadge} ${
                                historyItem.action === "status_change" 
                                  ? styles.historyActionBadgeStatus 
                                  : styles.historyActionBadgeComment
                              }`}>
                                <span className={styles.historyActionIcon}>
                                  {historyItem.action === "status_change" ? <FaExclamationCircle size={12} /> : <FaReply size={12} />}
                                </span>
                                <span>
                                  {historyItem.action === "status_change" 
                                    ? "Estado" 
                                    : "Comentario"
                                  }
                                </span>
                              </div>
                            </td>
                            <td className={styles.historyUser}>
                              <div className={styles.historyUserBadge}>
                                <span className={styles.historyUserIcon}>
                                  <FaUser size={12} />
                                </span>
                                <span>{historyItem.author}</span>
                              </div>
                            </td>
                            <td className={styles.historyDescription}>
                              {historyItem.description}
                            </td>
                            <td className={styles.historyDetails}>
                              <div className={styles.historyDetailsContent}>
                                {historyItem.action === "status_change" ? (
                                  <div className={styles.statusChangeContainer}>
                                    <span className={styles.statusChangeOld}>
                                      {historyItem.oldValue}
                                    </span>
                                    <span className={styles.statusChangeArrow}>→</span>
                                    <span className={styles.statusChangeNew}>
                                      {historyItem.newValue}
                                    </span>
                                  </div>
                                ) : (
                                  <div className={styles.commentContainer}>
                                    {historyItem.messageInternal && (
                                      <div style={{ marginBottom: 8 }}>
                                        <span style={{ fontSize: typography.sizes.xs, fontWeight: 400, color: colors.warningDark }}>Interna: </span>
                                        <span className={styles.commentText}>{historyItem.messageInternal}</span>
                                      </div>
                                    )}
                                    {(historyItem.messageClient || (!historyItem.messageInternal && historyItem.message)) && (
                                      <div>
                                        <span style={{ fontSize: typography.sizes.xs, fontWeight: 400, color: colors.successDark }}>Al residente: </span>
                                        <span className={styles.commentText}>
                                          {historyItem.messageClient || historyItem.message}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default IncidenciasEdificio;
